/**
 * API Service for web app
 * Centralized API client with authentication and token refresh
 */

const API_URL = process.env.REACT_APP_API_URL || '';

class ApiService {
  constructor() {
    this.accessToken = localStorage.getItem('accessToken');
    this.refreshToken = localStorage.getItem('refreshToken');
  }

  setTokens(tokens) {
    this.accessToken = tokens.access;
    this.refreshToken = tokens.refresh;
    localStorage.setItem('accessToken', tokens.access);
    if (tokens.refresh) {
      localStorage.setItem('refreshToken', tokens.refresh);
    }
  }

  clearTokens() {
    this.accessToken = null;
    this.refreshToken = null;
    localStorage.removeItem('accessToken');
    localStorage.removeItem('refreshToken');
    localStorage.removeItem('user');
  }

  getAccessToken() {
    return this.accessToken || localStorage.getItem('accessToken');
  }

  isAuthenticated() {
    return !!this.getAccessToken();
  }

  async request(endpoint, options = {}) {
    const url = `${API_URL}${endpoint}`;

    const headers = {
      'Content-Type': 'application/json',
      ...options.headers,
    };

    const token = this.getAccessToken();
    if (token) {
      headers['Authorization'] = `Bearer ${token}`;
    }

    let response = await fetch(url, {
      ...options,
      headers,
    });

    // If unauthorized, try to refresh token
    if (response.status === 401 && this.refreshToken) {
      const refreshed = await this.refreshAccessToken();
      if (refreshed) {
        headers['Authorization'] = `Bearer ${this.accessToken}`;
        response = await fetch(url, { ...options, headers });
      }
    }

    if (!response.ok) {
      const error = await response.json().catch(() => ({}));
      const errorMessage = error.detail || error.error || error.message || `Request failed (${response.status})`;
      throw new Error(errorMessage);
    }

    // Handle empty responses
    const text = await response.text();
    return text ? JSON.parse(text) : {};
  }

  async refreshAccessToken() {
    try {
      const response = await fetch(`${API_URL}/api/auth/refresh/`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ refresh: this.refreshToken }),
      });

      if (response.ok) {
        const data = await response.json();
        this.setTokens(data);
        return true;
      }
    } catch (error) {
      // Token refresh failed
    }

    this.clearTokens();
    return false;
  }

  // Auth endpoints
  async login(username, password) {
    const tokens = await this.request('/api/auth/login/', {
      method: 'POST',
      body: JSON.stringify({ username, password }),
    });
    this.setTokens(tokens);
    return tokens;
  }

  async register(email, password, firstName = '', lastName = '') {
    return this.request('/api/auth/register/', {
      method: 'POST',
      body: JSON.stringify({
        email,
        username: email,
        password,
        password_confirm: password,
        first_name: firstName,
        last_name: lastName,
      }),
    });
  }

  async googleSignIn(idToken) {
    const tokens = await this.request('/api/auth/google/', {
      method: 'POST',
      body: JSON.stringify({ id_token: idToken }),
    });
    this.setTokens(tokens);
    return tokens;
  }

  async getProfile() {
    return this.request('/api/auth/profile/');
  }

  async updateProfile(data) {
    return this.request('/api/auth/profile/', {
      method: 'PATCH',
      body: JSON.stringify(data),
    });
  }

  async logout() {
    if (this.refreshToken) {
      try {
        await this.request('/api/auth/logout/', {
          method: 'POST',
          body: JSON.stringify({ refresh: this.refreshToken }),
        });
      } catch (error) {
        // Ignore errors during logout
      }
    }
    this.clearTokens();
  }

  // Jobs
  async getJobs(clientId = null) {
    const endpoint = clientId ? `/api/jobs/?client_id=${clientId}` : '/api/jobs/';
    return this.request(endpoint);
  }

  async getJob(id) {
    return this.request(`/api/jobs/${id}/`);
  }

  async createJob(data) {
    return this.request('/api/jobs/', {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  async updateJob(id, data) {
    return this.request(`/api/jobs/${id}/`, {
      method: 'PATCH',
      body: JSON.stringify(data),
    });
  }

  async deleteJob(id) {
    return this.request(`/api/jobs/${id}/`, {
      method: 'DELETE',
    });
  }

  async duplicateJob(id) {
    return this.request(`/api/jobs/${id}/duplicate/`, {
      method: 'POST',
    });
  }

  // Job Notes
  async getJobNotes(jobId) {
    return this.request(`/api/jobs/${jobId}/notes/`);
  }

  async addJobNote(jobId, data) {
    return this.request(`/api/jobs/${jobId}/notes/`, {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  async deleteJobNote(jobId, noteId) {
    return this.request(`/api/jobs/${jobId}/notes/${noteId}/`, {
      method: 'DELETE',
    });
  }

  // Job Photos
  async getJobPhotos(jobId) {
    return this.request(`/api/jobs/${jobId}/photos/`);
  }

  // Clients
  async getClients() {
    return this.request('/api/clients/');
  }

  async getClient(id) {
    return this.request(`/api/clients/${id}/`);
  }

  async createClient(data) {
    return this.request('/api/clients/', {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  async updateClient(id, data) {
    return this.request(`/api/clients/${id}/`, {
      method: 'PATCH',
      body: JSON.stringify(data),
    });
  }

  async deleteClient(id) {
    return this.request(`/api/clients/${id}/`, {
      method: 'DELETE',
    });
  }

  // Change Orders
  async getChangeOrders() {
    return this.request('/api/change-orders/');
  }

  async getChangeOrder(id) {
    return this.request(`/api/change-orders/${id}/`);
  }

  // Stats
  async getUserStats() {
    return this.request('/api/auth/stats/');
  }

  // Export user data as CSV
  async exportData() {
    const url = `${API_URL}/api/auth/export-data/`;
    const token = this.getAccessToken();

    const response = await fetch(url, {
      headers: {
        'Authorization': `Bearer ${token}`,
      },
    });

    if (!response.ok) {
      throw new Error('Failed to export data');
    }

    return response.text();
  }

  // Delete account
  async deleteAccount() {
    return this.request('/api/auth/delete-account/', {
      method: 'DELETE',
    });
  }

  // Line Item Templates
  async getLineItemTemplates() {
    return this.request('/api/line-item-templates/');
  }

  async getLineItemTemplate(id) {
    return this.request(`/api/line-item-templates/${id}/`);
  }

  async createLineItemTemplate(data) {
    return this.request('/api/line-item-templates/', {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  async updateLineItemTemplate(id, data) {
    return this.request(`/api/line-item-templates/${id}/`, {
      method: 'PUT',
      body: JSON.stringify(data),
    });
  }

  async deleteLineItemTemplate(id) {
    return this.request(`/api/line-item-templates/${id}/`, {
      method: 'DELETE',
    });
  }

  async duplicateLineItemTemplate(id) {
    return this.request(`/api/line-item-templates/${id}/duplicate/`, {
      method: 'POST',
    });
  }

  // Reports
  async getReportSummary() {
    return this.request('/api/reports/summary/');
  }

  async getReportRevenue(period = 'month') {
    return this.request(`/api/reports/revenue/?period=${period}`);
  }

  async getReportJobs(period = 'month') {
    return this.request(`/api/reports/jobs/?period=${period}`);
  }

  async getReportClients(period = 'month') {
    return this.request(`/api/reports/clients/?period=${period}`);
  }

  // Google Calendar
  async getGoogleCalendarAuthUrl() {
    return this.request('/api/auth/google-calendar/connect/');
  }

  async getGoogleCalendarStatus() {
    return this.request('/api/auth/google-calendar/status/');
  }

  async disconnectGoogleCalendar() {
    return this.request('/api/auth/google-calendar/disconnect/', {
      method: 'POST',
    });
  }

  async getGoogleCalendars() {
    return this.request('/api/auth/google-calendar/calendars/');
  }

  async setGoogleCalendar(calendarId) {
    return this.request('/api/auth/google-calendar/set-calendar/', {
      method: 'POST',
      body: JSON.stringify({ calendar_id: calendarId }),
    });
  }

  async syncJobToCalendar(jobId) {
    return this.request(`/api/jobs/${jobId}/sync_calendar/`, {
      method: 'POST',
    });
  }

  // Quote methods
  async addQuoteLineItem(jobId, data) {
    return this.request(`/api/jobs/${jobId}/add_quote_line_item/`, {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  async deleteQuoteLineItem(jobId, lineItemId) {
    return this.request(`/api/jobs/${jobId}/delete_quote_line_item/`, {
      method: 'DELETE',
      body: JSON.stringify({ line_item_id: lineItemId }),
    });
  }

  async sendQuote(jobId, currencySymbol = '$', message = '', photoIds = []) {
    return this.request(`/api/jobs/${jobId}/send_quote/`, {
      method: 'POST',
      body: JSON.stringify({ currency_symbol: currencySymbol, message, photo_ids: photoIds }),
    });
  }

  async acceptQuote(jobId) {
    return this.request(`/api/jobs/${jobId}/accept_quote/`, {
      method: 'POST',
    });
  }

  // Invoice methods
  async getInvoices(jobId = null) {
    const endpoint = jobId ? `/api/invoices/?job_id=${jobId}` : '/api/invoices/';
    return this.request(endpoint);
  }

  async getInvoice(id) {
    return this.request(`/api/invoices/${id}/`);
  }

  async getInvoicePreviewData(jobId) {
    return this.request(`/api/jobs/${jobId}/get_invoice_preview_data/`);
  }

  async createInvoice(data) {
    return this.request('/api/invoices/', {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  async updateInvoice(id, data) {
    return this.request(`/api/invoices/${id}/`, {
      method: 'PATCH',
      body: JSON.stringify(data),
    });
  }

  async sendInvoice(invoiceId, photoIds = []) {
    return this.request(`/api/invoices/${invoiceId}/send_invoice/`, {
      method: 'POST',
      body: JSON.stringify({ photo_ids: photoIds }),
    });
  }

  async deleteInvoiceLineItem(invoiceId, lineItemId) {
    return this.request(`/api/invoices/${invoiceId}/delete_line_item/`, {
      method: 'DELETE',
      body: JSON.stringify({ line_item_id: lineItemId }),
    });
  }

  async addInvoiceLineItem(invoiceId, data) {
    return this.request(`/api/invoices/${invoiceId}/add_line_item/`, {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  async voidInvoice(id) {
    return this.request(`/api/invoices/${id}/void_invoice/`, {
      method: 'POST',
    });
  }

  async markInvoicePaid(id) {
    return this.request(`/api/invoices/${id}/mark_paid/`, {
      method: 'POST',
    });
  }
}

export const api = new ApiService();
export default api;
